
import Promise from "plugins/bluebird/index"
import _compact from "lodash/compact"
import Cookies from "plugins/cookies-js/index"

import q_ajax_3 from "app/utils/q_ajax/q_ajax_3"

import Modal from "app/modal/show-modal"
import Google_Event from "app/tracking/google/event"
import GA4_Event from "app/tracking/google/google_analytics_4/event"
import FacebookPixel_Event from "app/tracking/facebook-pixel/event"
import Auth_action from "app/authentication/action"
import Auth_callback_queue from "app/authentication/callback_queue"
import Concierge_Timer from "app/concierge/timer"
import PageInstance from "app/page_instance/index"
import ListingSearchOperations from "app/listings/search/operations"

import SaveManager from "concepts/listings/searches/saved/manager"

import {
  refreshAndUpdateComponentPromise,
} from "app/authentication/remote_state"

import {
  getLastContactInfoSyncPromise,
} from "app/authentication/contact_info_caching"

import {
  captureMessage,
  captureException,
} from "app/error_reporting/index"

import {
  getContactInfoData,
  setContactInfoData,
} from "app/local_storages/contact_info_storage/index"

import {
  create as create_user,
  ResultTypeEnums as CreateUserResultTypes,
} from "app/resources/users/create/index"

import {
  isPresentString,
} from "app/utils/type_checking"

import {
  isSignedIn,
} from "app/authentication/state"

import {
  create as create_listing_search,
  Inputs as CreateListingSearchInputs,
  ResultTypeEnums as CreateListingSearchResultTypes,
  ListingSearchDataSourceLevelTwoIdentifier,
} from "app/resources/listing_search/create/index"

import {
  trackSignUp,
} from "app/authentication/tracking"

import {
  CookiesKeys as AppSearchFilter_CookiesKeys,
  Filter as AppSearchFilter,
} from "app/search/filter"

import ListingSearchShareBox from "concepts/listings/searches/share/box/index"

import RecordWithSameCriteriaExistModal from "apps/spacious_website/components/listing_search/listing_search_creation_failure_message_modals/record_with_same_criteria_exist"
import OnlyDefaultCriteriaDetectedModal from "apps/spacious_website/components/listing_search/listing_search_creation_failure_message_modals/only_default_criteria_detected"

import I18n from "plugins/i18n-js"


const RejectionReasons = {
  ERROR_REPORTED:                   "ERROR_REPORTED",
  ONLY_DEFAULT_CRITERIA_DETECTED:   "ONLY_DEFAULT_CRITERIA_DETECTED",
  RECORD_WITH_SAME_CRITERIA_EXIST:  "RECORD_WITH_SAME_CRITERIA_EXIST",
}


class Button {
  protected data_source_level_two_identifier = null as null | ListingSearchDataSourceLevelTwoIdentifier


  public onSaveClick() {
    const serialized_form_data_listing_search = AppSearchFilter.getFilterOptions()
    serialized_form_data_listing_search.neighbourhood_ids =
      _compact(serialized_form_data_listing_search.neighbourhood_ids)

    const local_contact_info_data = getContactInfoData()

    if (! isPresentString(local_contact_info_data.contact_email_address)) {
      // Show something
      Auth_callback_queue.add(() => {
        getLastContactInfoSyncPromise()
        .then(() => this.onSaveClick())
        .done()
      }, "event:save_search")

      Auth_action.showSignUpModal({
        enable_account_already_exists_error_message: false,
      })
      .then(({$modal, authentication_box}) => {
        authentication_box.on("sign_up:fail:account_conflict", ({email_address}) => {
          $modal.modal("hide")
          // Retry AFTER setting local contact email
          setContactInfoData({
            contact_email_address: email_address,
          })
          this.onSaveClick()
        })
      })
      .done()
      return
    }

    let create_listing_search_inputs_for_debug = {}
    Promise.resolve()
    .then(() => {
      Modal.showLoadingIndicator()

      if (isSignedIn()) {
        return Promise.resolve()
      }

      return create_user({
        authentication_handle:
          local_contact_info_data.user_authentication_handle ||
          local_contact_info_data.contact_email_address ||
          "",
        email_address:
          local_contact_info_data.contact_email_address || "",
        sign_in_on_success: true,
      })
      .then(({result_type}) => {
        switch (result_type) {
          case CreateUserResultTypes.SUCCESS:
            trackSignUp()
            refreshAndUpdateComponentPromise().done()
            return
          case CreateUserResultTypes.CONFLICT:
          case CreateUserResultTypes.PARAMS_INVALID:
            return
          default:
            captureMessage(
              `unexpected/unhandled result_type: <${result_type}>`,
              {
                source_file_path:
                  "app/assets/javascripts/concepts/listings/contact/button/events/index",
              }
            )
            throw RejectionReasons.ERROR_REPORTED
        }
      })
    })
    .then(() => {
      const inputs: CreateListingSearchInputs = {
        // Need `||` to make flow happy
        user_authentication_handle:
          local_contact_info_data.user_authentication_handle ||
          local_contact_info_data.contact_email_address ||
          "",

        listing_search_attributes: serialized_form_data_listing_search,
        source_identifier:
          "concepts/listings/searches/filter_bar/desktop/save-search-button#onSaveClick",
      }
      if (this.data_source_level_two_identifier != null) {
        inputs.listing_search_data_source_level_two_identifier = this.data_source_level_two_identifier
      }
      create_listing_search_inputs_for_debug = inputs
      return create_listing_search(inputs)
    })
    .then(({result_type, data, http_response_status_code}) => {
      switch (result_type) {
        case CreateListingSearchResultTypes.SUCCESS:
          // show success modal
          return data
        case CreateListingSearchResultTypes.INVALID:
          return
        case CreateListingSearchResultTypes.UNAUTHENTICATED:
          return
        case CreateListingSearchResultTypes.RECORD_WITH_SAME_CRITERIA_EXIST:
          throw RejectionReasons.RECORD_WITH_SAME_CRITERIA_EXIST
        case CreateListingSearchResultTypes.ONLY_DEFAULT_CRITERIA_DETECTED:
          throw RejectionReasons.ONLY_DEFAULT_CRITERIA_DETECTED
        default:
          captureMessage(
            `unexpected/unhandled result_type from create_listing_search: <${result_type}>`,
            Object.assign({
              source_file_path:
                "concepts/listings/searches/filter_bar/desktop/save-search-button/index",
              http_response_status_code: http_response_status_code,
            }, create_listing_search_inputs_for_debug),
          )
          throw RejectionReasons.ERROR_REPORTED
      }
    })
    .then((data) => {
      if (ListingSearchOperations.is_high_value_search(serialized_form_data_listing_search)){
        Google_Event.track({
          category: "Remarketing",
          action:   "User Interested In >50m Properties",
        })

        FacebookPixel_Event.track({
          category: "Remarketing",
          name:     "User Interested In >50m Properties",
        })
      }

      Google_Event.track({
        category: PageInstance.currentPageNameForTracking(),
        action:   "Save Search",
        options:  {
          eventLabel: "Save Search - Saved",
        },
      })

      GA4_Event.track({
        event_name: "saved_search",
        options:    {
          page: PageInstance.currentPageNameForTracking(),
        },
      })

      Cookies.set(
        AppSearchFilter_CookiesKeys.SAVED,
        true,
        {
          // # expire in 1 day
          expires: 24 * 60 * 60,
        },
      )
      Concierge_Timer.clearTimer()

      return Promise.resolve(
        import(
          "plugins/js-routes"
        )
      )
      .then((Routes) => {
        return q_ajax_3({
          url: Routes.show_one_apps_spacious_website_partials_listing_search_creation_success_message_modals_path({
            // Not sure how to infer data type
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            listing_search_id:  (data as any).listing_search.id,
            locale:             I18n.locale,
          }),
          method:   "GET",
          dataType: "html",
        })
      })
    })
    .then(({data}) => {
      Modal.hideTopModal()
      Modal.showModalWithContent(data)
      ListingSearchShareBox.create()
      Google_Event.track({
        category: PageInstance.currentPageNameForTracking(),
        action:   "Share Search Modal (After Saving Search)",
        options:  {
          eventLabel: "Share Search Modal (After Saving Search) Pop Up",
        },
      })

      GA4_Event.track({
        event_name: "share_search_modal_displayed",
        options:    {
          page:   PageInstance.currentPageNameForTracking(),
          label:  "after_saved_search",
        },
      })
    })
    .catch((whatever) => {
      Modal.hideLoadingIndicator()

      if (whatever === RejectionReasons.ERROR_REPORTED) { return }
      if (whatever === RejectionReasons.RECORD_WITH_SAME_CRITERIA_EXIST) {
        RecordWithSameCriteriaExistModal.show()
        return
      }
      if (whatever === RejectionReasons.ONLY_DEFAULT_CRITERIA_DETECTED) {
        OnlyDefaultCriteriaDetectedModal.show()
        return
      }

      if (whatever instanceof Error) {
        captureException(
          whatever,
          {
            source_file_path:
              "concepts/listings/searches/filter_bar/desktop/save-search-button/index",
          }
        )
        return
      }

      captureMessage(
        whatever.toString(),
        {
          source_file_path:
            "concepts/listings/searches/filter_bar/desktop/save-search-button/index",
        }
      )
    })
    .done()
  }

  public setListingSearchDataSourceLevelTwoIdentifierAs(value: ListingSearchDataSourceLevelTwoIdentifier) {
    this.data_source_level_two_identifier = value
  }
}

const Factory = {
  _instance: null as null | Button,
  instance(): Button {
    if (this._instance != null) {
      return this._instance
    }

    this._instance = new Button()
    return this._instance
  },
}

const init = (): void => {
  SaveManager.on("save_click:action", () => {
    Factory.instance().onSaveClick()
  })
}

export default {init}
export {
  Factory,
  ListingSearchDataSourceLevelTwoIdentifier,
}
